import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Heading,
  I,
  Icon,
  Row,
  Stack,
} from '@leanin/ui';
import { FlowShell } from '@/components/onboarding/FlowShell';
import { trackOnboardingStepCompleted } from '@/lib/analytics/events';
import { flowStepParams } from '@/lib/analytics/onboarding-steps';
import { Picture } from '@/components/media/Picture';
import nlHubPreview from '@/assets/onboarding/nl-hub-preview.png?w=440;880;1320&format=avif;webp;png&as=picture';
import { NL_ROLE, NL_STEPS } from './-steps';
import styles from './hub.module.css';

// Network Leader onboarding tour — step 2 of 4.
// Explains what the Network Leaders Hub is and where to find it on
// the Networks directory.

const HUB_USES: ReadonlyArray<string> = [
  'Share ideas, challenges, and best practices',
  'Ask questions and get support from peers and the Lean In team',
  'Exchange resources and inspiration',
  'Learn from the experiences of other Network Leaders',
  'Stay informed about opportunities, events, and important updates',
];

export const Route = createFileRoute('/onboarding/nl/hub')({
  component: NLOnboardingHubPage,
});

function NLOnboardingHubPage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role={NL_ROLE}
      stepIndex={1}
      steps={NL_STEPS}
      minutes={3}
      title="The Network Leaders Hub"
      primary={
        <Button
          variant="primary"
          iconRight={I.arrow}
          onClick={() => {
            trackOnboardingStepCompleted(flowStepParams('/onboarding/nl/hub'));
            navigate({ to: '/onboarding/nl/network-page' });
          }}
        >
          Next
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/nl/welcome' })}
        >
          Back
        </Button>
      }
    >
      <Stack gap="lg">
        <Stack gap="md">
          <Heading level={2}>What is the Network Leaders Hub?</Heading>
          <Body size="md">
            The Network Leaders Hub is a dedicated space for Lean In Network
            Leaders to connect, collaborate, and learn from one another —
            while also staying connected to the Lean In team for guidance,
            support, and updates.
          </Body>
          <Body size="md">Inside the Hub, leaders can:</Body>
          <Stack gap="sm">
            {HUB_USES.map((use) => (
              <Row key={use} gap="sm" align="flex-start">
                <Icon size="sm" color="var(--primary)">
                  {I.check}
                </Icon>
                <Body size="md">{use}</Body>
              </Row>
            ))}
          </Stack>
        </Stack>

        <Card pad="flush" clip>
          <Picture
            image={nlHubPreview}
            className={styles['preview']}
            alt="Network Leaders Hub highlighted on the Networks directory"
            sizes="(max-width: 720px) 100vw, 880px"
          />
        </Card>
      </Stack>
    </FlowShell>
  );
}
